import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api";

const Comments = () => {
  const [comments, setComments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchComments = async (page = 1, append = false) => {
    try {
      append ? setLoadingMore(true) : setLoading(true);
      setError("");

      const response = await api.get("/admin/comments", {
        params: { page },
      });

      const { comments: newComments, pagination } = response.data;

      setComments((prev) => append ? [...prev, ...newComments] : newComments);
      setCurrentPage(pagination.page);
      setTotalPages(pagination.totalPages);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des commentaires.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchComments(1, false);
  }, []);

  const handleLoadMore = () => {
    if (currentPage < totalPages && !loadingMore) {
      fetchComments(currentPage + 1, true);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Es-tu sûr de vouloir supprimer ce commentaire ?")) return;

    try {
      setDeletingId(id);
      setError("");

      await api.delete(`/admin/comments/${id}`);

      setComments((prev) => prev.filter((comment) => comment.id !== id));
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.title ||
          "Erreur lors de la suppression du commentaire."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const filteredComments = comments.filter((comment) => {
    const search = searchTerm.toLowerCase();
    return (
      comment.id?.toString().includes(search) ||
      comment.content?.toLowerCase().includes(search) ||
      comment.user?.firstname?.toLowerCase().includes(search) ||
      comment.user?.lastname?.toLowerCase().includes(search) ||
      comment.user?.email?.toLowerCase().includes(search) ||
      comment.post?.title?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="container mx-auto px-4 py-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-lg">
            <p className="text-slate-300">Chargement en cours...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto p-4">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
              Dashboard commentaires
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">
              Gestion des commentaires
            </h1>
            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Consultez, recherchez et supprimez les commentaires publiés sur les posts.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-lg">
          <div className="mb-6">
            <input
              type="text"
              placeholder="Rechercher par ID, contenu, auteur, email ou titre du post..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 md:w-[500px]"
            />
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full bg-slate-900">
              <thead>
                <tr className="bg-slate-800 text-slate-200">
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">ID</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Auteur</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Email</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Commentaire</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Post lié</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Date</th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredComments.length > 0 ? (
                  filteredComments.map((comment) => (
                    <tr
                      key={comment.id}
                      className="align-top text-slate-300 transition hover:bg-slate-800/60"
                    >
                      <td className="border border-slate-800 px-4 py-3 text-center">
                        #{comment.id}
                      </td>
                      <td className="border border-slate-800 px-4 py-3">
                        {comment.user
                          ? `${comment.user.firstname || ""} ${comment.user.lastname || ""}`.trim()
                          : "Utilisateur inconnu"}
                      </td>
                      <td className="border border-slate-800 px-4 py-3">
                        {comment.user?.email || "—"}
                      </td>
                      <td className="border border-slate-800 px-4 py-3 max-w-md">
                        <p className="line-clamp-4 text-sm text-slate-300">
                          {comment.content || "Aucun contenu"}
                        </p>
                      </td>
                      <td className="border border-slate-800 px-4 py-3">
                        {comment.post ? (
                          <div className="flex flex-col gap-2">
                            <span className="text-sm text-slate-200">
                              {comment.post.title || `Post #${comment.postId}`}
                            </span>
                            {comment.post.slug && (
                              <Link
                                to={`/dashboard/posts/edit/${comment.post.slug}`}
                                className="inline-block w-fit rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-blue-600"
                              >
                                Voir le post
                              </Link>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-500">Aucun post</span>
                        )}
                      </td>
                      <td className="border border-slate-800 px-4 py-3 whitespace-nowrap text-sm text-slate-400">
                        {comment.createdAt
                          ? new Date(comment.createdAt).toLocaleDateString("fr-BE")
                          : "—"}
                      </td>
                      <td className="border border-slate-800 px-4 py-3">
                        <button
                          onClick={() => handleDelete(comment.id)}
                          disabled={deletingId === comment.id}
                          className="rounded-lg bg-red-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {deletingId === comment.id ? "Suppression..." : "Supprimer"}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="border border-slate-800 px-4 py-6 text-center text-slate-400"
                    >
                      Aucun commentaire trouvé.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {currentPage < totalPages && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-orange-300"
            >
              {loadingMore ? "Chargement..." : "Charger plus"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Comments;